import { useContext, useEffect, useState } from "react";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { GET_NOTIFICATION_HUB_TOKEN } from "graphql/queries";
import { LoggerService } from "services";
import { useDispatch } from "react-redux";
import { AlertTypes, INotification, NotificationTypes } from "helpers";
import { AppContext } from "helpers/contexts/AppContext";
import {
  setDocumentHubUpdate,
  setNewAlertComing,
  setUnreadAlertsNumber,
  setAgreementFormStatusChange,
  setPortalReportNotification,
  setPortalPgaFormReset,
  setFarmGroupStatusChange,
  setClientId,
} from "store";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const SignalR: React.FC = () => {
  const [connection, setConnection] = useState<null | HubConnection>(null);
  const { access_token, authenticated } = useContext(AppContext);
  const { refetch: getNotificationHubToken } = useQuery<any>(GET_NOTIFICATION_HUB_TOKEN, {
    skip: !access_token && !authenticated
  });
  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    if (access_token) {
      const connect = new HubConnectionBuilder()
        .withUrl(`${window.REACT_APP_API_GATEWAY_URL}/notification/hub`, {
          skipNegotiation: true,
          transport: 1, //1: WebSocket, 2 :SSE, 4: LongPoll
          accessTokenFactory: async () => {
            const response = await getNotificationHubToken();
            const token = response.data?.getTokenForNotificationHub?.hubToken;

            return token;
          },
        })
        .withAutomaticReconnect()
        .build();
      setConnection(connect);
    }
  }, [getNotificationHubToken, access_token]);

  const handleUnreadAlert = (notification: INotification | any) => {
    dispatch(setUnreadAlertsNumber(notification.data.numUnreadAlerts));
    
    const status = notification.data?.status;
    const alertType = notification.data?.modifiedAlert?.alertType;

    if (status === "New" && (alertType === AlertTypes.Alert || alertType === AlertTypes.PGAFormApproved)) {
      dispatch(setNewAlertComing(notification.data.modifiedAlert));
    }
  };

  const handleLoginSuccess = (notification: INotification | any) => {
    LoggerService.info(
      "SignalR LoginSuccessEvent: " + notification.data.clientId + "-" + notification.data.timeStamp
    );
    if (!notification.data.clientId || !notification.data.timeStamp) return;
    const signinTimeStamp = getCookie("signinTimeStamp");
    if (signinTimeStamp === notification.data.timeStamp) {
      dispatch(setClientId(notification.data.clientId));
    }
  };
  
  const handleAlertToDocumentHub = (_: INotification | any) => dispatch(setDocumentHubUpdate(Date.now()));
  const handleAgreementFormStatusChange = (_: INotification|any) => dispatch(setAgreementFormStatusChange(Date.now()));
  const handleToggleMaintenanceStatus = (_: INotification|any) => window.location.reload();
  const handleResetPasswordNotification = (_: INotification|any) => navigate("/auth/401");
  const handlePortalReport = (_: INotification|any) => dispatch(setPortalReportNotification(Date.now()));
  const handlePgaFormReset = (_: INotification|any) => dispatch(setPortalPgaFormReset(Date.now()));
  const handleFarmGroupStatusChange = (notification: INotification|any) => notification.data.farmGroupStatus && dispatch(setFarmGroupStatusChange(notification.data.farmGroupStatus));

  const notificationMap = new Map<NotificationTypes, (notification: INotification | any) => void>([
    [NotificationTypes.UnreadAlert, handleUnreadAlert],
    [NotificationTypes.LoginSuccessEvent, handleLoginSuccess],
    [NotificationTypes.AlertToDocumentHub, handleAlertToDocumentHub],
    [NotificationTypes.AgreementFormStatusChange, handleAgreementFormStatusChange],
    [NotificationTypes.ToggleMaintenanceStatus, handleToggleMaintenanceStatus],
    [NotificationTypes.ResetPasswordNotification, handleResetPasswordNotification],
    [NotificationTypes.PortalReport, handlePortalReport],
    [NotificationTypes.PgaFormReset, handlePgaFormReset],
    [NotificationTypes.FarmGroupStatusChange, handleFarmGroupStatusChange],
  ]);
  useEffect(() => {
    if (connection) {
      connection
        .start()
        .then(() => {
          connection.on("NOTIFICATION", (notification: INotification | any) => {
            const handler = notificationMap.get(notification.type);
            if (handler) handler(notification);
          });
        })
        .catch((error) => {
          LoggerService.error(error);
        });
    }

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [connection, dispatch, navigate]);
  return <></>;
};
export default SignalR;
